/**
 * Перетаскивание с «живым» местом: карточка приподнимается, соседи расступаются.
 *
 * Браузерный drag-and-drop (draggable + dragover) даёт полупрозрачный снимок
 * элемента и ничего больше: ни наклона, ни пустого места под ним, ни того,
 * как список расходится перед вставкой. Поэтому тащим сами, на указателе.
 *
 * Как это устроено:
 *  · нажали и подержали — начинается перетаскивание (короткое нажатие
 *    остаётся обычным щелчком: чекбоксы и кнопки внутри работают как всегда);
 *  · оригинал остаётся в потоке, но становится невидимым — его место и есть
 *    та самая пустота под карточкой;
 *  · по странице летит копия: чуть крупнее, с наклоном и тенью;
 *  · соседи разъезжаются сдвигом на высоту карточки — видно, куда она встанет;
 *  · отпустили — копия долетает до места и только потом список
 *    перестраивается: порядок меняется беззвучно, за уже сыгранной анимацией.
 *
 * Горизонтальные списки (колонки таблицы) работают так же: ось задаётся
 * параметром, вся математика общая.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

export type DragAxis = 'x' | 'y'

export interface DragSortOptions {
  /** Коды элементов в текущем порядке. */
  items: readonly string[]
  /** Новый порядок после того, как карточка встала на место. */
  onDrop: (order: string[], code: string) => void
  axis?: DragAxis
  /** Сколько держать до начала перетаскивания. */
  holdMs?: number
  /** Элемент нельзя двигать (закреплённая зона таблицы). */
  isFixed?: (code: string) => boolean
  /**
   * С чего перетаскивание НЕ начинается. По умолчанию — любые кнопки и поля
   * ввода: нажатие на них принадлежит им. В шапке таблицы сам заголовок —
   * кнопка сортировки, и там список другой.
   */
  ignoreSelector?: string
  /** Контейнер с прокруткой: у его краёв список подкручивается сам. */
  scroller?: () => HTMLElement | null
  /**
   * Отпустили за пределами своего списка (колонку бросили на чужой блок).
   * Вернул `true` — значит, случай разобран и порядок менять не нужно.
   */
  onDropOutside?: (code: string, x: number, y: number) => boolean
}

export interface DragSortApi {
  /** Запомнить узел элемента: ref на обёртку. */
  register: (code: string) => (el: HTMLElement | null) => void
  /** Начать слежение за нажатием. Вешается на сам элемент. */
  onPointerDown: (code: string, event: ReactPointerEvent) => void
  /** Что сейчас тащим. */
  dragging: string | null
  /** Куда встанет, если отпустить. */
  target: number | null
  /**
   * Где пройдёт линия вставки — координата в пикселях окна по оси
   * перетаскивания. `null`, когда ничего не тащим.
   */
  dropAt: number | null
}

interface Snapshot {
  code: string
  node: HTMLElement
  /** Положение до начала перетаскивания. */
  start: number
  size: number
  center: number
}

/** Насколько можно сдвинуть палец до начала перетаскивания — дальше это прокрутка. */
const SLOP = 8
/** Сколько летит карточка до своего места. */
const SETTLE_MS = 200

export function useDragSort(options: DragSortOptions): DragSortApi {
  const {
    items, onDrop, axis = 'y', holdMs = 160, isFixed, scroller, onDropOutside,
    ignoreSelector = 'button, input, select, textarea, a, [role="button"]',
  } = options

  const nodes = useRef(new Map<string, HTMLElement>())
  const [dragging, setDragging] = useState<string | null>(null)
  const [target, setTarget] = useState<number | null>(null)
  const [dropAt, setDropAt] = useState<number | null>(null)

  /* Всё состояние одного перетаскивания: его читают обработчики окна, а не
     рендер, поэтому оно живёт в ref, а не в state. */
  const session = useRef<{
    code: string
    from: number
    to: number
    ghost: HTMLElement
    source: HTMLElement
    shots: Snapshot[]
    step: number
    grabX: number
    grabY: number
    scrollEl: HTMLElement | null
    scrollStart: number
    lastX: number
    lastY: number
    moved: boolean
  } | null>(null)

  const latest = useRef({ items, onDrop, axis, isFixed, scroller, onDropOutside, ignoreSelector })
  latest.current = { items, onDrop, axis, isFixed, scroller, onDropOutside, ignoreSelector }

  const register = useCallback((code: string) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(code, el)
    else nodes.current.delete(code)
  }, [])

  /* Убираем за собой, если компонент исчез прямо во время перетаскивания. */
  useEffect(() => () => {
    const live = session.current
    if (!live) return
    live.ghost.remove()
    live.source.style.opacity = ''
    document.body.classList.remove('dnd-active')
  }, [])

  const onPointerDown = useCallback((code: string, event: ReactPointerEvent) => {
    if (event.button !== 0 || session.current) return
    const { items: list, axis: dir, isFixed: fixed, scroller: scrollerOf } = latest.current
    const ignore = latest.current.ignoreSelector
    if (fixed?.(code)) return

    /* Нажали на кнопку, чекбокс или ссылку — это их нажатие, а не перенос. */
    const target0 = event.target
    if (ignore !== '' && target0 instanceof Element && target0.closest(ignore)) return

    const node = nodes.current.get(code)
    if (!node) return

    const startX = event.clientX
    const startY = event.clientY
    const pointerType = event.pointerType
    let timer = 0

    const cleanupWatch = (): void => {
      window.clearTimeout(timer)
      window.removeEventListener('pointermove', watchMove)
      window.removeEventListener('pointerup', watchUp)
      window.removeEventListener('pointercancel', watchUp)
    }

    function watchMove(move: PointerEvent): void {
      if (Math.hypot(move.clientX - startX, move.clientY - startY) <= SLOP) return
      /* Мышью тащат сразу: ждать удержания, когда рука уже повела, —
         значит не поехать вовсе. Пальцем наоборот: там движение сначала
         означает прокрутку, и перенос начинается только по удержанию. */
      cleanupWatch()
      if (pointerType !== 'touch') {
        begin()
        moveTo(move.clientX, move.clientY)
      }
    }
    function watchUp(): void { cleanupWatch() }

    window.addEventListener('pointermove', watchMove)
    window.addEventListener('pointerup', watchUp)
    window.addEventListener('pointercancel', watchUp)

    timer = window.setTimeout(() => {
      cleanupWatch()
      begin()
    }, holdMs)

    function begin(): void {
      const order = list.filter((item) => !fixed?.(item))
      const from = order.indexOf(code)
      if (from < 0) return

      const shots: Snapshot[] = []
      for (const item of order) {
        const el = nodes.current.get(item)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        shots.push({
          code: item,
          node: el,
          start: dir === 'y' ? rect.top : rect.left,
          size: dir === 'y' ? rect.height : rect.width,
          center: dir === 'y' ? rect.top + rect.height / 2 : rect.left + rect.width / 2,
        })
      }
      const me = shots.find((shot) => shot.code === code)
      if (!me) return

      /* Шаг расступания — размер карточки вместе с зазором до соседа. */
      const index = shots.indexOf(me)
      const neighbour = shots[index + 1] ?? shots[index - 1]
      const gap = neighbour
        ? Math.max(0, Math.abs(neighbour.start - me.start) - me.size)
        : 0
      const step = me.size + gap

      const rect = me.node.getBoundingClientRect()
      const ghost = me.node.cloneNode(true) as HTMLElement
      ghost.classList.add('dnd-ghost')
      ghost.style.width = `${rect.width}px`
      ghost.style.height = `${rect.height}px`
      ghost.style.left = `${rect.left}px`
      ghost.style.top = `${rect.top}px`
      /* У закреплённой ячейки таблицы в разметке стоит `right`/`bottom` —
         у копии они спорили бы с положением под курсором. */
      ghost.style.right = 'auto'
      ghost.style.bottom = 'auto'
      document.body.appendChild(ghost)
      /* Прячем оригинал стилем, а не классом: className у этих узлов пишет
         React, и на ближайшей же перерисовке наш класс исчезал бы — место
         под карточкой переставало быть пустым. */
      me.node.style.opacity = '0'
      document.body.classList.add('dnd-active')

      const scrollEl = scrollerOf?.() ?? null
      session.current = {
        code,
        from,
        to: from,
        ghost,
        source: me.node,
        shots,
        step,
        grabX: startX,
        grabY: startY,
        scrollEl,
        scrollStart: scrollEl ? (dir === 'y' ? scrollEl.scrollTop : scrollEl.scrollLeft) : 0,
        lastX: startX,
        lastY: startY,
        moved: false,
      }
      setDragging(code)
      setTarget(from)

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
      moveTo(startX, startY)
    }

    function moveTo(x: number, y: number): void {
      const live = session.current
      if (!live) return
      live.lastX = x
      live.lastY = y
      if (Math.hypot(x - live.grabX, y - live.grabY) > 4) live.moved = true
      const { shots, from, step } = live
      const scrolled = live.scrollEl
        ? (dir === 'y' ? live.scrollEl.scrollTop : live.scrollEl.scrollLeft) - live.scrollStart
        : 0
      const dx = x - live.grabX
      const dy = y - live.grabY
      live.ghost.style.transform = `translate(${dx}px, ${dy}px) rotate(${dir === 'y' ? 1.6 : 0.8}deg) scale(1.03)`

      /* Куда встанет: считаем по центру летящей карточки. */
      const me = shots[from]!
      const centre = me.center + (dir === 'y' ? dy : dx) + scrolled
      let to = 0
      shots.forEach((shot, index) => {
        if (index === from) return
        if (centre > shot.center) to += 1
      })
      if (to !== live.to) {
        live.to = to
        setTarget(to)
      }
      /* Линия вставки: левый (верхний) край места, куда встанет карточка. */
      const slot = shots[to]!
      setDropAt(to <= from ? slot.start : slot.start + slot.size - me.size)

      /* Соседи расступаются — сдвигом, а не перестройкой списка. */
      shots.forEach((shot, index) => {
        if (index === from) return
        let shift = 0
        if (to > from && index > from && index <= to) shift = -step
        if (to < from && index >= to && index < from) shift = step
        shot.node.style.transform = shift === 0 ? '' : `translate${dir === 'y' ? 'Y' : 'X'}(${shift}px)`
        shot.node.style.transition = 'transform 180ms cubic-bezier(.2, .8, .3, 1)'
      })
    }

    function onMove(event2: PointerEvent): void {
      event2.preventDefault()
      /* У края прокручиваемой области список едет сам — иначе до дальних
         колонок не дотащить. */
      const live = session.current
      const box = live?.scrollEl
      if (box) {
        const rect = box.getBoundingClientRect()
        const near = 48
        if (dir === 'x') {
          if (event2.clientX > rect.right - near) box.scrollLeft += 14
          else if (event2.clientX < rect.left + near) box.scrollLeft -= 14
        } else {
          if (event2.clientY > rect.bottom - near) box.scrollTop += 14
          else if (event2.clientY < rect.top + near) box.scrollTop -= 14
        }
      }
      moveTo(event2.clientX, event2.clientY)
    }

    function onUp(): void {
      const live = session.current
      if (!live) return
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)

      const { shots, from, to, ghost } = live
      const me = shots[from]!
      const dragged = live.moved

      /* Щелчок после перетаскивания не наш: заголовок колонки — кнопка
         сортировки, и она не должна срабатывать от переноса. Перехватываем
         сразу: щелчок придёт следом за отпусканием, а не через анимацию.
         Нажатие без движения — именно щелчок: человек просто помедлил,
         и отнимать у него нажатие нельзя. */
      if (dragged) {
        const swallow = (click: MouseEvent): void => {
          click.preventDefault()
          click.stopPropagation()
        }
        window.addEventListener('click', swallow, { capture: true, once: true })
        window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 300)
      }

      /* Бросили на чужой блок — там своя логика, порядок внутри не трогаем. */
      const outside = latest.current.onDropOutside?.(code, live.lastX, live.lastY) ?? false
      if (outside) {
        ghost.classList.add('dnd-ghost--vanish')
      } else {
        /* Куда долететь: соседи уже разъехались, и свободное место начинается
           ровно там, где раньше стоял элемент с номером `to`. */
        const landing = to === from ? me.start : shots[to]!.start
        const delta = landing - me.start
        ghost.classList.add('dnd-ghost--settling')
        ghost.style.transform = `translate${dir === 'y' ? 'Y' : 'X'}(${delta}px)`
      }

      const finish = (): void => {
        ghost.remove()
        live.source.style.opacity = ''
        document.body.classList.remove('dnd-active')
        for (const shot of shots) {
          shot.node.style.transition = ''
          shot.node.style.transform = ''
        }
        session.current = null
        setDragging(null)
        setTarget(null)
        setDropAt(null)
        if (!outside && to !== from) {
          const order = [...latest.current.items]
          const movable = order.filter((item) => !fixed?.(item))
          movable.splice(from, 1)
          movable.splice(to, 0, code)
          /* Закреплённые остаются на своих местах, остальные встают по новому. */
          let cursor = 0
          latest.current.onDrop(order.map((item) => (fixed?.(item) ? item : movable[cursor++]!)), code)
        }
      }

      window.setTimeout(finish, SETTLE_MS)
    }
  }, [holdMs])

  return { register, onPointerDown, dragging, target, dropAt }
}
